import * as React from 'react'

import * as api from '@owl-nest/api-client/next'
import * as consent from '@owl-nest/consent'
import * as dateUtils from '@owl-nest/date'
import * as dateFns from '@owl-nest/date-fns'
import { t, tc, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'
import * as hooks from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'

type NewsCardProps = {
  isEditMode: boolean
  isVideoActive?: boolean
  locale: api.Lang
  news: api.News
  onVideoMount?: () => void
  href: string
  project: models.project.Project
}

export function NewsCard({
  isEditMode,
  isVideoActive = true,
  href,
  onVideoMount,
  locale,
  news,
  project,
}: NewsCardProps): React.ReactElement {
  const preview = models.i18n.getWithLang(news.preview, locale, project.lang)
  const title = models.i18n.getWithLang(news.title, locale, project.lang)

  const userHasReadAccess = Boolean(news.permissions.self?.includes('read'))

  return (
    <plume.NewsCard
      content={preview}
      editingLink={`/projects/${project.id}/backoffice/news/${news.id}/`}
      href={href}
      isEditMode={isEditMode}
      isLocked={!userHasReadAccess}
      isVideoActive={isVideoActive}
      media={getMedia(news, project, locale)}
      onVideoMount={onVideoMount}
      date={getDisplayedDate(news, locale)}
      title={title ?? ''}
      translations={{
        commentCount: tn('%(count)s comment', '%(count)s comments', news.comments_count, {
          count: plume.accounting.formatNumber(news.comments_count),
        }),
        editCTA: t('Edit'),
        locked: t('Locked'),
        readCTA: t('See news'),
        subscribeCTA: tc('Subscribe from [amount] / month', {
          amount: (
            <plume.LocaleMoneyDisplayer
              amount={getLowestPriceRewards(news, project)}
              currency={project.currency}
              localeFormat={locale}
              rates={env.CURRENCIES_RATES}
              targetCurrency={project.currency}
            />
          ),
        }),
      }}
    />
  )
}

function getMedia(news: api.News, project: api.Project, locale: api.Lang): plume.Media | undefined {
  const video = news.video && models.i18n.getWithLang(news.video, locale, project.lang)
  const image = news.image && models.i18n.getWithLang(news.image, locale, project.lang)

  // news.contentType can't be used because it's related to the lang
  // whereas we want to mix translated contents here
  const contentType = video ? 'video' : image ? 'image' : 'text'

  switch (contentType) {
    case 'video': {
      return {
        type: contentType,
        videoHTML: video?.html as string,
        consentWall: consent.media.Wall,
        placeholder: image?.versions?.['large'].url ?? video?.thumbnail_url ?? '',
      }
    }

    case 'image': {
      return {
        type: contentType,
        src: image?.versions?.['large'].url ?? '',
      }
    }

    default:
      return undefined
  }
}

function getDisplayedDate(news: api.News, locale: api.Lang): string | undefined {
  if (news.status === api.NewsStatus.ONLINE) {
    return dateUtils.format(dateUtils.parseISO(news.date_publication ?? news.date_creation), 'PPP')
  }

  if (news.status === api.NewsStatus.WAITING) {
    return t('Draft (only visible by you)')
  }

  if (news.status === api.NewsStatus.SCHEDULED && news.date_schedule) {
    const yesterday = dateFns.sub(new Date(), { days: 1 })

    const dateSchedule = dateUtils.parseISO(news.date_schedule)

    const isDateScheduleAfterYesterday = dateFns.isAfter(dateSchedule, yesterday)

    const formattedScheduledDate = isDateScheduleAfterYesterday
      ? dateUtils.format(dateSchedule, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
      : ` ${t('on')} ${dateUtils.format(dateSchedule, 'P')}`

    return t('Post scheduled for %(date)s at %(time)s', {
      date: formattedScheduledDate,
      time: dateUtils.format(dateSchedule, 'HH:mm'),
    })
  }
}

function getLowestPriceRewards(news: api.News, project: api.Project): number | null {
  const userHasReadAccess = Boolean(news.permissions.self?.includes('read'))

  return !userHasReadAccess
    ? news.audience_type === api.NewsAudienceType.PAYING
      ? project.lowest_contribution_amount
      : news.rewards?.reduce((acc, reward) => {
          if (acc === -1) {
            return reward.price
          }
          if (reward.price < acc) {
            return reward.price
          }
          return acc
        }, -1) ?? null
    : null
}
